import { useState } from "react";
import { useData } from "../context/DataContext";
import { Plus, Trash2 } from "lucide-react";
import { Modal, Button, Input, Select, EmptyState } from "./ui";
import { useDeleteConfirmation, useFormReset } from "../utils/hooks";

const GekoppelteKlassen = () => {
  const { data, addGekoppelt, deleteGekoppelt } = useData();

  const [showModal, setShowModal] = useState(false);
  const { formData, setFormData, resetForm } = useFormReset({
    klasse1Id: "",
    klasse2Id: "",
    fachKuerzel: "",
    schuelerTotal: "0",
    bemerkungen: "",
  });

  const confirmDelete = useDeleteConfirmation("Kopplung", deleteGekoppelt);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.klasse1Id || !formData.klasse2Id) {
      alert("Bitte beide Klassen wählen!");
      return;
    }
    addGekoppelt({
      klasse1Id: formData.klasse1Id,
      klasse2Id: formData.klasse2Id,
      fachKuerzel: formData.fachKuerzel,
      schuelerTotal: parseInt(formData.schuelerTotal),
      bemerkungen: formData.bemerkungen,
    });
    resetForm();
    setShowModal(false);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap gap-3">
        <Button
          onClick={() => setShowModal(true)}
          variant="primary"
          icon={<Plus className="w-4 h-4" />}
        >
          Neue Kopplung
        </Button>
      </div>

      {data.gekoppelt.length === 0 ? (
        <EmptyState
          icon="🔗"
          title="Keine gekoppelten Klassen vorhanden."
        />
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Klasse 1
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Klasse 2
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Fach
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Schüler Total
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Bemerkungen
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Aktionen
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {data.gekoppelt.map((gk) => {
                  const kl1 = data.klassen.find((k) => k.id === gk.klasse1Id);
                  const kl2 = data.klassen.find((k) => k.id === gk.klasse2Id);

                  return (
                    <tr
                      key={gk.id}
                      className="hover:bg-gray-50 transition-colors"
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {kl1 ? kl1.name : "?"}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {kl2 ? kl2.name : "?"}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                          {gk.fachKuerzel}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-900">
                        {gk.schuelerTotal}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {gk.bemerkungen || "-"}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <button
                          onClick={() => confirmDelete(gk.id)}
                          className="inline-flex items-center p-1.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Löschen"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <Modal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title="Neue Kopplung"
      >
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <Select
            label="Klasse 1 *"
            required
            value={formData.klasse1Id}
            onChange={(e) =>
              setFormData({ ...formData, klasse1Id: e.target.value })
            }
          >
            <option value="">- Bitte wählen -</option>
            {data.klassen.map((kl) => (
              <option key={kl.id} value={kl.id}>
                {kl.name}
              </option>
            ))}
          </Select>
          <Select
            label="Klasse 2 *"
            required
            value={formData.klasse2Id}
            onChange={(e) =>
              setFormData({ ...formData, klasse2Id: e.target.value })
            }
          >
            <option value="">- Bitte wählen -</option>
            {data.klassen.map((kl) => (
              <option key={kl.id} value={kl.id}>
                {kl.name}
              </option>
            ))}
          </Select>
          <Input
            label="Fach Kürzel *"
            type="text"
            required
            value={formData.fachKuerzel}
            onChange={(e) =>
              setFormData({ ...formData, fachKuerzel: e.target.value })
            }
            placeholder="z.B. BG, Mu"
          />
          <Input
            label="Schüler Total *"
            type="number"
            required
            min="0"
            value={formData.schuelerTotal}
            onChange={(e) =>
              setFormData({ ...formData, schuelerTotal: e.target.value })
            }
          />
          <Input
            label="Bemerkungen"
            type="text"
            value={formData.bemerkungen}
            onChange={(e) =>
              setFormData({ ...formData, bemerkungen: e.target.value })
            }
            placeholder='z.B. "8+8=16"'
          />
          <div className="flex gap-3 pt-4 border-t border-gray-200">
            <Button type="submit" variant="primary" className="flex-1">
              Speichern
            </Button>
            <Button
              type="button"
              variant="secondary"
              onClick={() => setShowModal(false)}
              className="flex-1"
            >
              Abbrechen
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default GekoppelteKlassen;
